import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { T } from '../customer/customers.theme';
import {
  Dialog,
  DialogHeader,
  ErrorLine,
  PillField,
  PrimaryButton,
} from '../customer/CustomersUI';
import { setToken } from '../../SignupAndLogin/loginSlice';
import { changePin } from './accountApi';

/**
 * Bedel Pin-ka.
 *
 * ── What was wrong with the old one ─────────────────────────────────────────
 *
 * The three fields were `type="text"`, so a PIN was typed in the clear on a
 * till that sits on a counter with customers on the other side of it.
 *
 * And it signed you out afterwards. That reads like caution, but the server
 * hands back a FRESH TOKEN from this very call — saving a password moves
 * `passwordChangedAt` and invalidates the old one, so it issues a replacement.
 * Throwing that away sent a manager to the login screen mid-shift for no
 * reason. The new token is adopted instead and the session continues.
 *
 * The wording is the phone's, which is Somali here.
 */

const COPY = {
  title: 'Bedel Pin-ka',
  current: 'Geli Pin-ki hore',
  next: 'Geli Pin-ka cusub',
  confirm: 'Ku celi pin-ka cusub',
  save: 'Bedel Pin-ka',

  currentRequired: 'Geli Pin-ki hore',
  nextRequired: 'Geli Pin-ka cusub',
  mismatch: 'Isma laha labada pin',
  same: 'Pin-ka cusub waa isku mid pin-ki hore',
  failed: 'Could not change the pin. Try again.',

  note: 'You stay signed in on this till. Other machines will ask for the new pin.',
  done: 'Pin-ka waa la badalay',
};

export default function ChangePinDialog({ user, onClose, onSaved }) {
  const dispatch = useDispatch();
  const token = useSelector((state) => state.login.token);

  const [current, setCurrent] = React.useState('');
  const [next, setNext] = React.useState('');
  const [confirm, setConfirm] = React.useState('');

  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState('');

  const submit = async () => {
    if (busy) return;

    if (!current.trim()) return setError(COPY.currentRequired);
    if (!next.trim()) return setError(COPY.nextRequired);
    // Checked here rather than at the server, which would reject the pair with
    // a message about `passwordConfirm` that means nothing to a cashier.
    if (next !== confirm) return setError(COPY.mismatch);
    if (next === current) return setError(COPY.same);

    setBusy(true);
    setError('');

    try {
      const fresh = await changePin({
        id: user._id || user.id,
        currentPin: current,
        newPin: next,
        token,
      });

      // The old token stopped working the moment the password saved, so this
      // swap is what keeps the session alive rather than a convenience.
      if (fresh) dispatch(setToken(fresh));

      if (onSaved) onSaved(COPY.done);
      onClose();
    } catch (err) {
      setError(err?.response?.data?.message || COPY.failed);
      setBusy(false);
    }
  };

  return (
    <Dialog onClose={onClose} locked={busy} width={430}>
      <div style={{ padding: '22px 24px 24px' }}>
        <DialogHeader title={COPY.title} onClose={onClose} locked={busy} />

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, paddingTop: 18 }}>
          {/* Masked, unlike the old form. This till stands on a counter. */}
          <PillField
            name="pin-current"
            type="password"
            value={current}
            onChange={(value) => {
              setCurrent(value);
              setError('');
            }}
            onEnter={submit}
            placeholder={COPY.current}
            disabled={busy}
            autoFocus
          />

          <PillField
            name="pin-next"
            type="password"
            value={next}
            onChange={(value) => {
              setNext(value);
              setError('');
            }}
            onEnter={submit}
            placeholder={COPY.next}
            disabled={busy}
          />

          <PillField
            name="pin-confirm"
            type="password"
            value={confirm}
            onChange={(value) => {
              setConfirm(value);
              setError('');
            }}
            onEnter={submit}
            placeholder={COPY.confirm}
            disabled={busy}
            invalid={Boolean(confirm) && confirm !== next}
          />
        </div>

        <div style={{ fontSize: 11.5, color: T.inkFaint, lineHeight: 1.6, paddingTop: 10 }}>
          {COPY.note}
        </div>

        <div style={{ paddingTop: 14 }}>
          <ErrorLine>{error}</ErrorLine>
        </div>

        <PrimaryButton
          onClick={submit}
          busy={busy}
          style={{ width: '100%', marginTop: error ? 12 : 18 }}
        >
          {COPY.save}
        </PrimaryButton>
      </div>
    </Dialog>
  );
}
